/**
 * BulaCoffeePassPage - Comprehensive landing page for the $95 annual coffee pass
 * Fully SEO optimized for Google, Bing, and AI search platforms
 * Includes gift pass purchase and redemption, streak reward program
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coffee, Clock, Calendar, CheckCircle, Star, Gift, 
  ArrowRight, Sparkles, Shield, CreditCard, AlertCircle,
  Share2, Copy, Facebook, MessageCircle, Mail, X, Heart,
  Award, Target, Flame, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import api from '@/utils/apiClient';

// Jack's of Fiji Gift Card Image - use local asset or generic placeholder
const JACKS_GIFT_CARD_IMAGE = "/images/jacks-gift-card.png";

// Coffee Pass Logo Component (SVG)
const BulaCoffeePassLogo = ({ className = "", size = 120 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 120 120" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <circle cx="60" cy="60" r="58" stroke="currentColor" strokeWidth="2" className="text-primary" />
    <circle cx="60" cy="60" r="52" stroke="currentColor" strokeWidth="1" className="text-primary/30" />
    <path 
      d="M40 45 H70 C72 45 74 47 74 49 V70 C74 75 70 80 65 80 H45 C40 80 36 75 36 70 V49 C36 47 38 45 40 45Z" 
      fill="currentColor" 
      className="text-primary"
    />
    <path 
      d="M74 52 H78 C82 52 85 55 85 59 V61 C85 65 82 68 78 68 H74" 
      stroke="currentColor" 
      strokeWidth="3" 
      fill="none"
      className="text-primary"
    />
    <path d="M45 40 Q47 35 45 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-primary/60" />
    <path d="M55 38 Q57 32 55 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-primary/60" />
    <path d="M65 40 Q67 35 65 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-primary/60" />
    <text x="60" y="18" textAnchor="middle" fontSize="10" fontWeight="bold" className="fill-primary">BULA</text>
    <text x="60" y="108" textAnchor="middle" fontSize="9" fontWeight="600" className="fill-primary">COFFEE PASS</text>
    <circle cx="25" cy="60" r="3" fill="currentColor" className="text-amber-500" />
    <circle cx="95" cy="60" r="3" fill="currentColor" className="text-amber-500" />
  </svg>
);

// Feature card component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-card border border-border rounded-2xl p-6 text-center"
  >
    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
      <Icon className="w-6 h-6 text-primary" />
    </div>
    <h3 className="font-heading text-lg mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground">{description}</p>
  </motion.div>
);

// Benefit item component
const BenefitItem = ({ icon: Icon, title, description, highlight = false }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    className={`flex items-start gap-4 p-4 rounded-xl ${highlight ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' : 'bg-muted/30'}`}
  >
    <div className={`p-2 rounded-lg ${highlight ? 'bg-amber-500/20' : 'bg-primary/10'}`}>
      <Icon className={`w-5 h-5 ${highlight ? 'text-amber-600' : 'text-primary'}`} />
    </div>
    <div>
      <h4 className={`font-medium ${highlight ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}>{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  </motion.div>
);

export default function BulaCoffeeClubPage() {
  const [searchParams] = useSearchParams();
  const giftCodeFromUrl = searchParams.get('gift');
  
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [existingPass, setExistingPass] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [redemptionWindow, setRedemptionWindow] = useState(null);
  
  // Gift pass state
  const [activeTab, setActiveTab] = useState(giftCodeFromUrl ? 'redeem-gift' : 'join');
  const [giftFormData, setGiftFormData] = useState({
    buyer_email: '',
    buyer_name: '',
    recipient_name: '',
    gift_message: ''
  });
  const [giftPurchaseResult, setGiftPurchaseResult] = useState(null);
  const [giftRedeemCode, setGiftRedeemCode] = useState(giftCodeFromUrl || '');
  const [giftCodeInfo, setGiftCodeInfo] = useState(null);
  const [checkingGiftCode, setCheckingGiftCode] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Fetch redemption window status
  useEffect(() => {
    const fetchWindow = async () => {
      try {
        const res = await api.get('/coffee-club/redemption-window');
        setRedemptionWindow(res.data);
      } catch (err) {
        console.error('Failed to fetch redemption window:', err);
      }
    };
    fetchWindow();
  }, []);

  // Check if user already has a pass
  const checkExistingPass = async () => {
    if (!formData.email && !formData.phone) return;
    
    setCheckingStatus(true);
    try {
      const params = new URLSearchParams();
      if (formData.email) params.append('email', formData.email);
      if (formData.phone) params.append('phone', formData.phone);
      
      const res = await api.get(`/coffee-club/status?${params}`);
      setExistingPass(res.data);
    } catch (err) {
      console.error('Failed to check status:', err);
    } finally {
      setCheckingStatus(false);
    }
  };

  // Handle pass purchase
  const handlePurchase = async (e) => {
    e.preventDefault();
    
    // Online payment is not yet configured - direct users to purchase at the cafe
    toast.info('Online payment coming soon!', {
      description: 'Visit Bulaccino Cafe to purchase your Bula Coffee Pass. We accept cash and card at the cafe!'
    });
  };

  // Check gift code validity
  const checkGiftCode = async (code) => {
    if (!code || code.length < 6) return;
    
    setCheckingGiftCode(true);
    try {
      const res = await api.get(`/coffee-club/gift/${encodeURIComponent(code)}`);
      setGiftCodeInfo(res.data);
    } catch (err) {
      setGiftCodeInfo({ error: true });
    } finally {
      setCheckingGiftCode(false);
    }
  };

  // Check gift code from URL on mount
  useEffect(() => {
    if (giftCodeFromUrl) {
      checkGiftCode(giftCodeFromUrl);
    }
  }, [giftCodeFromUrl]);

  // Purchase gift pass
  const handleGiftPurchase = async (e) => {
    e.preventDefault();
    
    // Online gift purchase is not yet configured - direct users to the cafe
    toast.info('Gift passes available at our cafe!', {
      description: 'Visit Bulaccino Cafe to purchase a gift pass. We\'ll help you create a special gift for someone you love!'
    });
  };

  // Redeem gift pass
  const handleGiftRedeem = async (e) => {
    e.preventDefault();
    
    if (!giftRedeemCode || !formData.email || !formData.phone || !formData.first_name || !formData.last_name) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/coffee-club/gift/redeem', {
        gift_code: giftRedeemCode,
        ...formData
      });

      const data = res.data;
      
      toast.success(data.message, {
        description: data.gift_message || `A gift from ${data.gifted_by}`
      });
      setExistingPass({
        has_active_pass: true,
        expiry_date: data.expiry_date,
        member_name: `${formData.first_name} ${formData.last_name}`,
        gifted_by: data.gifted_by,
        gift_message: data.gift_message
      });
      setActiveTab('join');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem gift');
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Social share helper
  const handleShare = (platform) => {
    if (!giftPurchaseResult) return;
    
    const urls = giftPurchaseResult.social_share;
    let url = '';
    
    switch (platform) {
      case 'facebook':
        url = urls.facebook;
        break;
      case 'twitter':
        url = urls.twitter;
        break;
      case 'whatsapp':
        url = urls.whatsapp;
        break;
      case 'messenger':
        url = urls.messenger;
        break;
      case 'email':
        window.location.href = urls.email;
        return;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
  };

  // JSON-LD Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Bula Coffee Pass",
    "description": "Annual coffee subscription pass at Bulaccino Cafe, Fiji. Get 1 FREE 8oz hot coffee every day for 365 days. Redeem daily between 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM. Hot coffee only. $95 per year. Complete 365 consecutive days to earn a $100 Jack's of Fiji gift card bonus!",
    "brand": {
      "@type": "Brand",
      "name": "Bulaccino Cafe"
    },
    "offers": {
      "@type": "Offer",
      "price": "95.00",
      "priceCurrency": "FJD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31",
      "itemCondition": "https://schema.org/NewCondition"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127"
    },
    "image": "https://bulaccinocafe.com/images/bula-coffee-pass.jpg"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the Bula Coffee Pass?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Bula Coffee Pass is a $95 annual subscription that gives you 1 FREE 8oz hot coffee every single day for 365 days at Bulaccino Cafe in Nadi, Fiji. You can redeem your free coffee between 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM daily - two convenient windows to choose from!"
        }
      },
      {
        "@type": "Question",
        "name": "What coffee can I get with the Bula Coffee Pass?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can choose from our hot coffee selection including Cappuccino, Latte, Flat White, Americano, Mocha, and more. The pass covers an 8oz hot coffee serving, with an optional upgrade to 12oz for just $3."
        }
      },
      {
        "@type": "Question",
        "name": "What are the redemption hours for the Bula Coffee Pass?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can redeem your free daily coffee between 7:00 AM - 10:00 AM OR 1:00 PM - 3:30 PM Fiji time, every day of the year. Two convenient windows - perfect for your morning routine or afternoon pick-me-up!"
        }
      },
      {
        "@type": "Question",
        "name": "What is the 365-Day Streak Reward?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "If you redeem your free coffee every single day for 365 consecutive days, you earn a 100 Jack's of Fiji gift card as a bonus reward! Consistency pays off - this reward celebrates your dedication to the Bula Coffee Pass."
        }
      },
      {
        "@type": "Question",
        "name": "Can I gift a Bula Coffee Pass to someone?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! You can purchase a gift pass and receive a unique code (BULA-XXXXXX) to share via WhatsApp, Facebook, Email, or any messaging app. The recipient redeems the code on our website to activate their pass."
        }
      },
      {
        "@type": "Question",
        "name": "Where is Bulaccino Cafe located?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Bulaccino Cafe is located on Main Street, Nadi, Fiji - right in the heart of town. We're easy to find and open early for your morning coffee fix!"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Bula Coffee Pass - $95/Year for Daily Free Coffee | Bulaccino Cafe Fiji</title>
        <meta name="description" content="Get 1 FREE 8oz hot coffee EVERY DAY for a whole year with Bula Coffee Pass at Bulaccino Cafe, Nadi Fiji. Only $95. Redeem 7-10 AM or 1-3:30 PM daily. Hot coffee only. 365-day streak earns $100 Jack's of Fiji gift card!" />
        <meta name="keywords" content="Fiji coffee pass, Nadi cafe, Bulaccino, free coffee Fiji, coffee subscription Fiji, Bula Coffee Pass, daily coffee deal, Fiji tourism, best coffee Nadi" />
        <link rel="canonical" href="https://bulaccinocafe.com/bula-coffee-club" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Bula Coffee Pass - 365 Days of Free Coffee for $95" />
        <meta property="og:description" content="Join Bula Coffee Pass at Bulaccino Cafe, Fiji. 1 FREE hot coffee every day for a year. Hot coffee only. Complete 365 days for a $100 bonus!" />
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://bulaccinocafe.com/bula-coffee-club" />
        <meta property="og:image" content="https://bulaccinocafe.com/images/bula-coffee-pass-og.jpg" />
        <meta property="og:locale" content="en_FJ" />
        <meta property="product:price:amount" content="95.00" />
        <meta property="product:price:currency" content="currency" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bula Coffee Pass - Daily Free Coffee at Bulaccino Cafe Fiji" />
        <meta name="twitter:description" content="$95 for 365 days of free hot coffee. 7-10 AM or 1-3:30 PM daily. Streak bonus: $100 gift card!" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-amber-500/5" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <BulaCoffeePassLogo size={100} />
                <div>
                  <p className="text-sm text-primary font-medium uppercase tracking-wider">Premium Membership</p>
                  <h1 className="font-heading text-4xl md:text-5xl">Bula Coffee Pass</h1>
                </div>
              </div>

              {/* Price Badge */}
              <div className="bg-gradient-to-r from-primary to-primary/80 text-white rounded-2xl p-6 mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold">$95</span>
                  <span className="text-xl opacity-90">per year</span>
                </div>
                <p className="mt-2 opacity-90">1 FREE 8oz coffee every day for 365 days</p>
              </div>

              {/* Redemption Window */}
              <div className="bg-card border border-border rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Clock className={`w-6 h-6 ${redemptionWindow?.is_open ? 'text-green-500' : 'text-amber-500'}`} />
                  <div>
                    <p className={`font-medium ${redemptionWindow?.is_open ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                      {redemptionWindow?.is_open ? 'Redemption Window Open!' : 'Redemption: 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {redemptionWindow?.message || 'Daily redemption window for your free coffee'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Benefits */}
              <ul className="space-y-3 mb-6">
                {[
                  'Hot coffee only (not iced)',
                  'Redeem daily 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM',
                  '+$3 upgrade to 12oz available',
                  'Valid for 12 months',
                  'No pause, no transfers - just great coffee!'
                ].map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Right - Sign Up Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-card border border-border rounded-3xl p-8 shadow-xl">
                {existingPass?.has_active_pass ? (
                  /* Already a member */
                  <div className="text-center py-8">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Star className="w-10 h-10 text-primary" />
                    </div>
                    <h3 className="font-heading text-2xl mb-2">You're a Member!</h3>
                    <p className="text-muted-foreground mb-4">
                      Welcome back, {existingPass.member_name}
                    </p>
                    {existingPass.gifted_by && (
                      <div className="bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-xl p-3 mb-4">
                        <p className="text-sm text-pink-700 dark:text-pink-300 flex items-center justify-center gap-2">
                          <Heart className="w-4 h-4" />
                          A gift from {existingPass.gifted_by}
                        </p>
                        {existingPass.gift_message && (
                          <p className="text-xs text-pink-600 dark:text-pink-400 mt-1 italic">
                            "{existingPass.gift_message}"
                          </p>
                        )}
                      </div>
                    )}
                    <div className="bg-muted/50 rounded-xl p-4 mb-4">
                      <p className="text-sm text-muted-foreground">Pass expires</p>
                      <p className="font-medium">
                        {new Date(existingPass.expiry_date).toLocaleDateString('en-FJ', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {existingPass.can_redeem 
                        ? '✓ You can redeem your free coffee now!'
                        : existingPass.already_redeemed_today
                          ? 'You already enjoyed your free coffee today'
                          : `${existingPass.window_message}`
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Tab Navigation */}
                    <div className="flex mb-6 bg-muted/50 rounded-xl p-1">
                      <button
                        onClick={() => setActiveTab('join')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'join' 
                            ? 'bg-background shadow-sm text-foreground' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="tab-join"
                      >
                        <CreditCard className="w-4 h-4 inline mr-1.5" />
                        Join
                      </button>
                      <button
                        onClick={() => setActiveTab('gift')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'gift' 
                            ? 'bg-background shadow-sm text-foreground' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="tab-gift"
                      >
                        <Gift className="w-4 h-4 inline mr-1.5" />
                        Gift
                      </button>
                      <button
                        onClick={() => setActiveTab('redeem-gift')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'redeem-gift' 
                            ? 'bg-background shadow-sm text-foreground' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="tab-redeem-gift"
                      >
                        <Sparkles className="w-4 h-4 inline mr-1.5" />
                        Redeem Gift
                      </button>
                    </div>

                    <AnimatePresence mode="wait">
                      {/* JOIN TAB */}
                      {activeTab === 'join' && (
                        <motion.form 
                          key="join"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          onSubmit={handlePurchase}
                        >
                          <div className="text-center mb-6">
                            <h2 className="font-heading text-2xl mb-2">Join the Pass</h2>
                            <p className="text-muted-foreground text-sm">
                              Start enjoying free coffee every morning
                            </p>
                          </div>

                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="first_name">First Name</Label>
                                <Input
                                  id="first_name"
                                  value={formData.first_name}
                                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                  placeholder="John"
                                  required
                                  data-testid="coffee-club-first-name"
                                />
                              </div>
                              <div>
                                <Label htmlFor="last_name">Last Name</Label>
                                <Input
                                  id="last_name"
                                  value={formData.last_name}
                                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                  placeholder="Doe"
                                  required
                                  data-testid="coffee-club-last-name"
                                />
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="email">Email</Label>
                              <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                onBlur={checkExistingPass}
                                placeholder="john@example.com"
                                required
                                data-testid="coffee-club-email"
                              />
                            </div>

                            <div>
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input
                                id="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                onBlur={checkExistingPass}
                                placeholder="+679 XXX XXXX"
                                required
                                data-testid="coffee-club-phone"
                              />
                            </div>

                            <div className="bg-muted/50 rounded-xl p-4 text-sm">
                              <div className="flex items-start gap-2">
                                <Shield className="w-4 h-4 text-primary mt-0.5" />
                                <div>
                                  <p className="font-medium text-foreground">Pass Terms</p>
                                  <p className="text-muted-foreground">
                                    Non-refundable - Non-transferable - Tied to your account
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Visit Cafe CTA - Online payment not yet configured */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 text-center border-2 border-amber-300 dark:border-amber-700">
                              <div className="flex items-center justify-center gap-2 mb-2">
                                <Coffee className="w-5 h-5 text-amber-600" />
                                <span className="font-bold text-amber-700 dark:text-amber-400">Available NOW at Our Cafe!</span>
                              </div>
                              <p className="text-sm text-foreground dark:text-foreground mb-2 font-medium">
                                Bula Coffee Pass - $95
                              </p>
                              <p className="text-sm text-muted-foreground mb-3">
                                📍 <strong>Namaka, Nadi</strong> - Corner of Concave Dr & Queens Road
                              </p>
                              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-3">
                                <p className="text-xs text-muted-foreground">
                                  🎉 Current price: <strong className="text-primary">$95</strong>
                                </p>
                              </div>
                              <a 
                                href="https://maps.google.com/?q=Bulaccino+Cafe+Namaka+Nadi+Fiji"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                              >
                                Get Directions to Namaka, Nadi <ArrowRight className="w-4 h-4" />
                              </a>
                            </div>

                            <Button
                              type="submit"
                              className="w-full rounded-full h-12 text-lg bg-amber-500 hover:bg-amber-600 text-white"
                              disabled={loading || checkingStatus}
                              data-testid="coffee-club-purchase-btn"
                            >
                              <Coffee className="w-5 h-5 mr-2" />
                              Visit Namaka Cafe - $95
                            </Button>

                            <p className="text-xs text-center text-muted-foreground">
                              By joining, you agree to our Terms of Use
                            </p>
                          </div>
                        </motion.form>
                      )}

                      {/* GIFT TAB */}
                      {activeTab === 'gift' && (
                        <motion.form 
                          key="gift"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          onSubmit={handleGiftPurchase}
                        >
                          <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center mx-auto mb-3">
                              <Gift className="w-8 h-8 text-pink-500" />
                            </div>
                            <h2 className="font-heading text-2xl mb-2">Gift a Coffee Pass</h2>
                            <p className="text-muted-foreground text-sm">
                              Give someone a year of free morning coffee!
                            </p>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="buyer_name">Your Name</Label>
                              <Input
                                id="buyer_name"
                                value={giftFormData.buyer_name}
                                onChange={(e) => setGiftFormData({...giftFormData, buyer_name: e.target.value})}
                                placeholder="Your name"
                                required
                                data-testid="gift-buyer-name"
                              />
                            </div>

                            <div>
                              <Label htmlFor="buyer_email">Your Email</Label>
                              <Input
                                id="buyer_email"
                                type="email"
                                value={giftFormData.buyer_email}
                                onChange={(e) => setGiftFormData({...giftFormData, buyer_email: e.target.value})}
                                placeholder="your@email.com"
                                required
                                data-testid="gift-buyer-email"
                              />
                            </div>

                            <div>
                              <Label htmlFor="recipient_name">Recipient's Name (optional)</Label>
                              <Input
                                id="recipient_name"
                                value={giftFormData.recipient_name}
                                onChange={(e) => setGiftFormData({...giftFormData, recipient_name: e.target.value})}
                                placeholder="Who is this gift for?"
                                data-testid="gift-recipient-name"
                              />
                            </div>

                            <div>
                              <Label htmlFor="gift_message">Personal Message (optional)</Label>
                              <Textarea
                                id="gift_message"
                                value={giftFormData.gift_message}
                                onChange={(e) => setGiftFormData({...giftFormData, gift_message: e.target.value})}
                                placeholder="Add a heartfelt message..."
                                className="resize-none"
                                rows={3}
                                data-testid="gift-message"
                              />
                            </div>

                            <div className="bg-pink-50 dark:bg-pink-900/20 rounded-xl p-4 text-sm border border-pink-200 dark:border-pink-800">
                              <div className="flex items-center justify-center gap-2 mb-2">
                                <Gift className="w-5 h-5 text-pink-600" />
                                <span className="font-medium text-pink-700 dark:text-pink-300">Gift Passes Available In-Store!</span>
                              </div>
                              <p className="text-pink-600 dark:text-pink-400 text-center mb-2">
                                Visit Bulaccino Cafe to purchase a gift pass for someone special.
                              </p>
                              <ul className="text-pink-600 dark:text-pink-400 text-xs space-y-1">
                                <li>- We'll create a beautiful gift code for you</li>
                                <li>- Share it via WhatsApp, Facebook, or any way you like</li>
                                <li>- Recipient activates it online with their details</li>
                              </ul>
                            </div>

                            <Button
                              type="submit"
                              className="w-full rounded-full h-12 text-lg bg-pink-500 hover:bg-pink-600"
                              disabled={loading}
                              data-testid="gift-purchase-btn"
                            >
                              <Gift className="w-5 h-5 mr-2" />
                              Visit Cafe to Purchase - $95
                            </Button>
                          </div>
                        </motion.form>
                      )}

                      {/* REDEEM GIFT TAB */}
                      {activeTab === 'redeem-gift' && (
                        <motion.form 
                          key="redeem-gift"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          onSubmit={handleGiftRedeem}
                        >
                          <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-3">
                              <Sparkles className="w-8 h-8 text-amber-500" />
                            </div>
                            <h2 className="font-heading text-2xl mb-2">Redeem Your Gift</h2>
                            <p className="text-muted-foreground text-sm">
                              Someone special bought you free coffee for a year!
                            </p>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="gift_code">Gift Code</Label>
                              <Input
                                id="gift_code"
                                value={giftRedeemCode}
                                onChange={(e) => {
                                  setGiftRedeemCode(e.target.value.toUpperCase());
                                  if (e.target.value.length >= 6) {
                                    checkGiftCode(e.target.value);
                                  }
                                }}
                                placeholder="BULA-XXXXXX"
                                className="text-center font-mono text-lg tracking-wider"
                                required
                                data-testid="gift-redeem-code"
                              />
                              {checkingGiftCode && (
                                <p className="text-xs text-muted-foreground mt-1">Checking code...</p>
                              )}
                              {giftCodeInfo?.is_valid && (
                                <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" />
                                    Valid gift code!
                                    {giftCodeInfo.buyer_name && ` From ${giftCodeInfo.buyer_name}`}
                                  </p>
                                  {giftCodeInfo.gift_message && (
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 italic">
                                      "{giftCodeInfo.gift_message}"
                                    </p>
                                  )}
                                </div>
                              )}
                              {giftCodeInfo?.error && (
                                <p className="text-xs text-red-500 mt-1">Invalid or already redeemed code</p>
                              )}
                              {giftCodeInfo && !giftCodeInfo.is_valid && !giftCodeInfo.error && (
                                <p className="text-xs text-amber-500 mt-1">This code has already been redeemed</p>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="redeem_first_name">First Name</Label>
                                <Input
                                  id="redeem_first_name"
                                  value={formData.first_name}
                                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                  placeholder="John"
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="redeem_last_name">Last Name</Label>
                                <Input
                                  id="redeem_last_name"
                                  value={formData.last_name}
                                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                  placeholder="Doe"
                                  required
                                />
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="redeem_email">Your Email</Label>
                              <Input
                                id="redeem_email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                placeholder="your@email.com"
                                required
                              />
                            </div>

                            <div>
                              <Label htmlFor="redeem_phone">Your Phone</Label>
                              <Input
                                id="redeem_phone"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                placeholder="+679 XXX XXXX"
                                required
                              />
                            </div>

                            <Button
                              type="submit"
                              className="w-full rounded-full h-12 text-lg bg-amber-500 hover:bg-amber-600"
                              disabled={loading || !giftCodeInfo?.is_valid}
                              data-testid="gift-redeem-btn"
                            >
                              {loading ? (
                                'Activating...'
                              ) : (
                                <>
                                  <Sparkles className="w-5 h-5 mr-2" />
                                  Activate My Gift Pass
                                </>
                              )}
                            </Button>
                          </div>
                        </motion.form>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>

              {/* Share Modal */}
              <AnimatePresence>
                {showShareModal && giftPurchaseResult && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setShowShareModal(false)}
                  >
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0 }}
                      className="bg-card border border-border rounded-3xl p-8 max-w-md w-full shadow-2xl relative"
                      onClick={e => e.stopPropagation()}
                      data-testid="gift-share-modal"
                    >
                      <button
                        onClick={() => setShowShareModal(false)}
                        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-5 h-5" />
                      </button>

                      <div className="text-center mb-6">
                        <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="w-10 h-10 text-green-500" />
                        </div>
                        <h3 className="font-heading text-2xl mb-2">Gift Ready to Share!</h3>
                        <p className="text-muted-foreground text-sm">
                          Share this code with your lucky recipient
                        </p>
                      </div>

                      {/* Gift Code Display */}
                      <div className="bg-primary/10 rounded-2xl p-6 mb-6 text-center">
                        <p className="text-sm text-muted-foreground mb-2">Gift Code</p>
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-3xl font-mono font-bold text-primary tracking-wider">
                            {giftPurchaseResult.gift_code}
                          </span>
                          <button
                            onClick={() => copyToClipboard(giftPurchaseResult.gift_code)}
                            className="p-2 rounded-lg bg-primary/20 hover:bg-primary/30 transition-colors"
                            title="Copy code"
                          >
                            <Copy className="w-5 h-5 text-primary" />
                          </button>
                        </div>
                      </div>

                      {/* Share Buttons */}
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-center mb-3">Share via</p>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            variant="outline"
                            className="h-12 gap-2"
                            onClick={() => handleShare('whatsapp')}
                            data-testid="share-whatsapp"
                          >
                            <MessageCircle className="w-5 h-5 text-green-500" />
                            WhatsApp
                          </Button>
                          <Button
                            variant="outline"
                            className="h-12 gap-2"
                            onClick={() => handleShare('facebook')}
                            data-testid="share-facebook"
                          >
                            <Facebook className="w-5 h-5 text-blue-600" />
                            Facebook
                          </Button>
                          <Button
                            variant="outline"
                            className="h-12 gap-2"
                            onClick={() => handleShare('twitter')}
                            data-testid="share-twitter"
                          >
                            <Share2 className="w-5 h-5" />
                            Twitter/X
                          </Button>
                          <Button
                            variant="outline"
                            className="h-12 gap-2"
                            onClick={() => handleShare('email')}
                            data-testid="share-email"
                          >
                            <Mail className="w-5 h-5 text-red-500" />
                            Email
                          </Button>
                        </div>

                        <Button
                          variant="ghost"
                          className="w-full"
                          onClick={() => copyToClipboard(`${giftPurchaseResult.share_text} ${giftPurchaseResult.share_url}`)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Share Message
                        </Button>
                      </div>

                      <p className="text-xs text-center text-muted-foreground mt-6">
                        The recipient can redeem this code at<br />
                        <span className="text-primary font-medium">bulaccinocafe.com/bula-coffee-club</span>
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
          
          {/* Location Disclaimer - Below the grid */}
          <p className="text-xs text-center text-muted-foreground mt-8">
            All promotions and menu pricing are exclusive to Bulaccino Cafe located at Shop 3 at Concave Road, Namaka, Nadi, Fiji and not any other location.
          </p>
        </div>
        
      </section>

      {/* 365-Day Streak Reward Section */}
      <section className="py-16 bg-gradient-to-r from-amber-50 via-amber-100/50 to-amber-50 dark:from-amber-900/20 dark:via-amber-800/10 dark:to-amber-900/20 border-y border-amber-200 dark:border-amber-800">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-700 dark:text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Flame className="w-4 h-4" />
              EXCLUSIVE STREAK BONUS
            </div>
            
            <h2 className="font-heading text-3xl md:text-4xl mb-4">
              365-Day Streak Reward
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              <strong>Consistency pays off!</strong> Visit Bulaccino Cafe every single day for an entire year 
              to redeem your free coffee and earn an incredible reward.
            </p>

            <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
              {/* Left - Jack's Gift Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-xl border border-amber-200 dark:border-amber-800">
                  <img 
                    src={JACKS_GIFT_CARD_IMAGE}
                    alt="100 Jack's of Fiji Gift Card - 365-Day Streak Reward"
                    className="w-full h-auto rounded-xl"
                  />
                  <div className="mt-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">100</p>
                    <p className="text-sm text-muted-foreground">Jack's of Fiji Gift Card</p>
                  </div>
                </div>
                <div className="absolute -top-3 -right-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  BONUS!
                </div>
              </motion.div>

              {/* Right - How it Works */}
              <div className="text-left space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Target className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">The Challenge</h4>
                    <p className="text-sm text-muted-foreground">
                      Redeem your free 8oz coffee every single day for 365 consecutive days
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Calendar className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">The Timeframe</h4>
                    <p className="text-sm text-muted-foreground">
                      Visit daily within the 7:00 AM - 10:00 AM OR 1:00 PM - 3:30 PM redemption windows
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">The Reward</h4>
                    <p className="text-sm text-muted-foreground">
                      Complete 365 days and receive a <strong>100 Jack's of Fiji gift card</strong> - 
                      Fiji's premier department store!
                    </p>
                  </div>
                </div>

                <div className="bg-amber-100 dark:bg-amber-900/30 rounded-xl p-4 mt-6">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    <strong>Why Jack's of Fiji?</strong> Jack's is Fiji's most beloved department store, 
                    offering everything from fashion to electronics. Your 100 gift card is your reward 
                    for being our most dedicated coffee lover!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-muted/30" id="how-it-works">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl mb-4">How It Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple, straightforward, and delicious. Here's everything you need to know about the Bula Coffee Pass.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { icon: CreditCard, title: "1. Join", description: "Pay $95 one-time for your annual pass" },
              { icon: Calendar, title: "2. Visit Daily", description: "Come in any day 7-10 AM or 1-3:30 PM" },
              { icon: Coffee, title: "3. Redeem", description: "Get your free 8oz coffee (any type!)" },
              { icon: Sparkles, title: "4. Enjoy", description: "Upgrade to 12oz for just $3 extra" }
            ].map((step, i) => (
              <FeatureCard key={i} {...step} />
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Benefits */}
      <section className="py-16" id="benefits">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl mb-4">All the Benefits</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Your Bula Coffee Pass is packed with value. Here's everything included:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            <BenefitItem 
              icon={Coffee}
              title="365 Free Coffees"
              description="One 8oz coffee every single day for an entire year - that's potentially 1,825 in value!"
            />
            <BenefitItem 
              icon={Star}
              title="Any Coffee Type"
              description="Choose from Cappuccino, Latte, Flat White, Americano, Mocha, Espresso, and more"
            />
            <BenefitItem 
              icon={Sparkles}
              title="Hot or Iced"
              description="Your choice! Get your free coffee hot on cool mornings or iced when it's warm"
            />
            <BenefitItem 
              icon={TrendingUp}
              title="3 Upgrade Option"
              description="Want a larger size? Upgrade from 8oz to 12oz for just 3"
            />
            <BenefitItem 
              icon={Clock}
              title="Two Daily Windows"
              description="Redeem 7:00 AM - 10:00 AM OR 1:00 PM - 3:30 PM daily - morning routine or afternoon pick-me-up!"
            />
            <BenefitItem 
              icon={Gift}
              title="Gift-able"
              description="Purchase a gift pass for friends and family - share the coffee love!"
            />
            <BenefitItem 
              icon={Award}
              title="365-Day Streak Bonus"
              description="Complete 365 consecutive days and earn a 100 Jack's of Fiji gift card!"
              highlight={true}
            />
            <BenefitItem 
              icon={Shield}
              title="Simple & Secure"
              description="Tied to your account - no physical card to carry or lose"
            />
          </div>
        </div>
      </section>

      {/* Full Terms & Conditions */}
      <section className="py-16 bg-muted/30" id="terms">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl mb-6 text-center">Terms & Conditions</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-2xl p-6">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  What's Included
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>- 1 free 8oz coffee per day</li>
                  <li>- Hot coffee only (iced not included)</li>
                  <li>- Hot or iced - your choice</li>
                  <li>- 12-month validity from purchase date</li>
                  <li>- 3 upgrade to 12oz</li>
                  <li>- Eligible for 365-day streak bonus</li>
                </ul>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Important Rules
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>- Redemption hours: 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM</li>
                  <li>- Non-refundable after purchase</li>
                  <li>- Non-transferable to another person</li>
                  <li>- Cannot be paused or extended</li>
                  <li>- 1 redemption per day maximum</li>
                  <li>- Valid at Bulaccino Cafe, Nadi only</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
              <h3 className="font-medium mb-3 flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <Flame className="w-5 h-5" />
                365-Day Streak Bonus Rules
              </h3>
              <ul className="space-y-2 text-sm text-amber-700 dark:text-amber-400">
                <li>- Must redeem coffee on 365 consecutive days</li>
                <li>- Each redemption must be within the 7:00 AM - 10:00 AM or 1:00 PM - 3:30 PM window</li>
                <li>- Missing even one day resets the streak counter</li>
                <li>- Streak is automatically tracked in your account</li>
                <li>- $100 Jack's of Fiji gift card awarded upon completion</li>
                <li>- Gift card delivered within 7 days of completing the streak</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16" id="faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "What coffee can I get with my Bula Coffee Pass?",
                a: "You can choose ANY coffee type on our menu - Cappuccino, Latte, Flat White, Americano, Mocha, Espresso, Long Black, and more. Hot or iced, it's your choice every day!"
              },
              {
                q: "Can I get a larger size?",
                a: "Yes! Your pass includes a free 8oz coffee, but you can upgrade to 12oz for just 3 extra. Perfect for those mornings when you need a bigger boost."
              },
              {
                q: "What if I miss a day for the 365-day streak?",
                a: "Unfortunately, missing even one day will reset your streak counter. But don't worry - you can still enjoy your free daily coffee for the rest of your pass validity. The streak bonus is an extra reward for our most dedicated members."
              },
              {
                q: "Can I redeem outside the morning or afternoon window?",
                a: "No, redemptions are strictly limited to 7:00 AM - 10:00 AM Fiji time. This ensures we can serve all our Coffee Pass members efficiently during the morning rush."
              },
              {
                q: "What happens when my pass expires?",
                a: "Your pass is valid for 12 months from purchase date. After expiry, you can simply purchase a new pass to continue enjoying free coffee!"
              },
              {
                q: "Can I gift a Bula Coffee Pass?",
                a: "Absolutely! Click the 'Gift' tab above to purchase a gift pass. You'll receive a unique code to share via WhatsApp, Facebook, email, or any way you like."
              }
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <h3 className="font-medium mb-2">{faq.q}</h3>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <BulaCoffeePassLogo className="mx-auto mb-6" size={80} />
            <h2 className="font-heading text-3xl mb-4">Ready for 365 Days of Coffee?</h2>
            <p className="text-muted-foreground mb-6">
              Join the Bula Coffee Pass today and start your journey to free daily coffee - 
              plus the chance to earn a 100 Jack's of Fiji gift card!
            </p>
            <Button 
              size="lg" 
              className="rounded-full px-8"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Coffee className="w-5 h-5 mr-2" />
              Join Now - 40
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
